import { useState } from "react";
import { Link } from "react-router-dom";
import { t } from "../constants/i18n";
import type { TranslationKey } from "../constants/i18n";
import { FILTERS } from "../constants/projects";
import { STATUS_META } from "../constants/status";
import { useRemoteProjects } from "../hooks/useRemoteData";
import type { Lang } from "../constants/i18n";
import type { ProjectStatus } from "../constants/projects";
import "./Projects.css";

interface ProjectsProps {
  lang: Lang;
}

const statusColor = (s: ProjectStatus) => STATUS_META[s]?.color ?? "var(--text-dim)";

export default function Projects({ lang }: ProjectsProps) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");
  const { data: PROJECTS } = useRemoteProjects();
  const filtered = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div className="projects-page">
      <div className="projects-hero">
        <div className="container">
          <h1 className="projects-title">{t(lang, "projects_title")}</h1>
          <p className="projects-subtitle">{t(lang, "projects_subtitle")}</p>
        </div>
      </div>
      <div className="projects-body">
        <div className="container">
          <div className="filters" role="group" aria-label={t(lang, "projects_filter_label")}>
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                className={`filter-btn${filter === f ? " active" : ""}`}
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
              >
                {t(lang, `projects_filter_${f}` as TranslationKey)}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <p className="projects-empty">{t(lang, "projects_empty")}</p>
          ) : (
            <div className="projects-grid">
              {filtered.map((p) => {
                const color = statusColor(p.status);
                const statusLabel =
                  (lang === "jp" ? STATUS_META[p.status]?.label_jp : STATUS_META[p.status]?.label_en) ??
                  p.status;
                return (
                  <Link key={p.id} to={`/projects/${p.slug}`} className="project-card">
                    <div className="project-card-top">
                      <span className="project-name">{p.name}</span>
                      <span className="project-status" style={{ color, borderColor: color + "44" }}>
                        {statusLabel}
                      </span>
                    </div>
                    <p className="project-desc">{p.desc[lang] ?? p.desc.en}</p>
                    <div className="project-footer">
                      <div className="project-tags">
                        {p.tags.map((tag) => (
                          <span key={tag} className="project-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                      {p.isPrivate ? (
                        <span className="project-private">
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                          </svg>
                          {t(lang, "card_private")}
                        </span>
                      ) : (
                        <span className="project-arrow">→</span>
                      )}
                    </div>
                    <span className="project-year">{p.year}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
